import React, { memo, useState, useEffect, useMemo } from "react";
import { LineChart } from "@mui/x-charts";

function CashFlow({ yearDropDown, combinedData = [], loading: externalLoading = false }) {
  const [loading, setLoading] = useState(true);
  const [cashFlows, setCashFlows] = useState({
    inflows: [],
    outflows: [],
    months: [],
  });

  const parseDate = React.useCallback((dateString) => {
    // Handle different date formats
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split("/");
      return new Date(`${year}-${month}-${day}`);
    } else if (dateString.includes('-')) {
      return new Date(dateString);
    }
    return new Date(dateString);
  }, []);

  const transformDataToCashFlow = React.useCallback((data) => {
    const inflows = [];
    const outflows = [];

    data.forEach(item => {
      const cashFlowItem = {
        date: item.date || item.createdAt,
        amount: item.amount || 0,
        category: item.category || 'Other',
        description: item.description || item.particular || ''
      };

      if (item.type === 'income') {
        inflows.push(cashFlowItem);
      } else if (item.type === 'expense') {
        outflows.push(cashFlowItem);
      }
    });

    return { inflows, outflows };
  }, []);

  const groupDataByMonth = React.useCallback((data, rangeMonths, dropdown) => {
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];

    let startMonthIndex, startYear;
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    switch (dropdown) {
      case "This Fiscal Year":
        startMonthIndex = 0;
        startYear = currentYear;
        break;
      case "Previous Fiscal Year":
        startMonthIndex = 0;
        startYear = currentYear - 1;
        break;
      case "Last 12 Months":
        startMonthIndex = currentDate.getMonth() - 11;
        startYear = currentYear;
        if (startMonthIndex < 0) {
          startMonthIndex += 12;
          startYear -= 1;
        }
        break;
      case "Last 6 Months":
        startMonthIndex = currentDate.getMonth() - 5;
        startYear = currentYear;
        if (startMonthIndex < 0) {
          startMonthIndex += 12;
          startYear -= 1;
        }
        break;
      default:
        startMonthIndex = 0;
        startYear = currentYear;
    }

    const adjustedStartDate = new Date(startYear, startMonthIndex, 1);
    const dynamicMonths = [];

    for (let i = 0; i < rangeMonths; i++) {
      const date = new Date(adjustedStartDate);
      date.setMonth(adjustedStartDate.getMonth() + i);
      const monthName = monthNames[date.getMonth()];
      dynamicMonths.push(monthName);
    }

    const monthlyData = Array(rangeMonths).fill(0);

    data.forEach((item) => {
      const date = parseDate(item.date);
      if (date >= adjustedStartDate) {
        const diffInMonths =
          date.getMonth() -
          adjustedStartDate.getMonth() +
          12 * (date.getFullYear() - adjustedStartDate.getFullYear());
        if (diffInMonths >= 0 && diffInMonths < rangeMonths) {
          monthlyData[diffInMonths] += item.amount;
        }
      }
    });

    return { months: dynamicMonths, totals: monthlyData };
  }, [parseDate]);

  // Memoize the processed data to prevent unnecessary recalculations
  const processedData = useMemo(() => {
    if (!combinedData || combinedData.length === 0) {
      return {
        inflows: [],
        outflows: []
      };
    }

    try {
      // Transform combined data to cash flow format
      const { inflows: allInflows, outflows: allOutflows } = transformDataToCashFlow(combinedData);

      return {
        inflows: allInflows,
        outflows: allOutflows
      };
    } catch (error) {
      console.error("Error processing data:", error);
      return {
        inflows: [],
        outflows: []
      };
    }
  }, [combinedData, transformDataToCashFlow]);

  // Update cashFlows when processedData changes
  useEffect(() => {
    setCashFlows({
      inflows: processedData.inflows,
      outflows: processedData.outflows,
      months: []
    });
    setLoading(externalLoading);
  }, [processedData, externalLoading]);

  const rangeMonths =
    yearDropDown === "Last 12 Months"
      ? 12
      : yearDropDown === "Last 6 Months"
        ? 6
        : 12;

  const groupedInflows = useMemo(() =>
    groupDataByMonth(cashFlows.inflows, rangeMonths, yearDropDown),
    [cashFlows.inflows, rangeMonths, yearDropDown, groupDataByMonth]
  );

  const groupedOutflows = useMemo(() =>
    groupDataByMonth(cashFlows.outflows, rangeMonths, yearDropDown),
    [cashFlows.outflows, rangeMonths, yearDropDown, groupDataByMonth]
  );

  const netIncome = useMemo(() =>
    groupedInflows.totals.map((inflow, index) => inflow - groupedOutflows.totals[index]),
    [groupedInflows.totals, groupedOutflows.totals]
  );

  const totalInflow = useMemo(() =>
    groupedInflows.totals.reduce((acc, val) => acc + val, 0),
    [groupedInflows.totals]
  );

  const totalOutflow = useMemo(() =>
    groupedOutflows.totals.reduce((acc, val) => acc + val, 0),
    [groupedOutflows.totals]
  );

  const totalNetCashFlow = useMemo(() =>
    netIncome.reduce((acc, val) => acc + val, 0),
    [netIncome]
  );

  return (
    <div>
      {loading ? (
        <div className="flex flex-col items-center mt-8">
          {/* Skeleton loading state */}
          <div className="relative w-full h-60 bg-gray-50">
            {/* Line points loading */}
            <div className="animate-pulse absolute w-full h-full">
              {/* Points placeholder */}
              <div className="flex justify-evenly h-full px-2">
                {Array.from({ length: 8 }).map((_, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col items-center justify-end h-full"
                  >
                    {/* Placeholder point */}
                    <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                    {/* Placeholder vertical bar */}
                    <div
                      className={`bg-gray-300 rounded-full w-1 ${idx % 2 === 0 ? "h-10" : "h-16"
                        }`}
                    ></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <LineChart
            margin={{ top: 27, left: 60, right: 10, bottom: 45 }}
            xAxis={[
              {
                data: groupedInflows.months,
                scaleType: "band",
                categoryGapRatio: 0.3,
              },
            ]}
            yAxis={[
              {
                valueFormatter: (value) => {
                  if (value >= 1000000 || value <= -1000000) {
                    return `${(value / 1000000).toFixed(0)}M`;
                  } else if (value >= 1000 || value <= -1000) {
                    return `${(value / 1000).toFixed(0)}k`;
                  }
                  return value.toString();
                },
              },
            ]}
            series={[
              {
                curve: "natural",
                data: groupedInflows.totals,
                label: "Inflows",
                color: "#016FC4",
              },
              {
                curve: "natural",
                data: groupedOutflows.totals,
                label: "Outflows",
                color: "#3AC0DA",
              },
            ]}
            height={280}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: -6,
                itemMarkWidth: 20,
                itemMarkHeight: 10,
              },
            }}
          />

          <div className="flex flex-col">
            <div className="border-t border-gray-300 my-4"></div>

            <div className="flex justify-evenly items-center w-full">
              <div className="flex flex-col items-center text-center">
                <p className="font-semibold text-gray-700">Total Inflows</p>
                <p className="text-gray-700 text-lg font-bold">
                  Nu. {totalInflow.toLocaleString()}
                </p>
              </div>
              <div className="border-l border-gray-300 h-12"></div>
              <div className="flex flex-col items-center text-center">
                <p className="font-semibold text-gray-700">Total Outflows</p>
                <p className="text-gray-700 text-lg font-bold">
                  Nu. {totalOutflow.toLocaleString()}
                </p>
              </div>
              <div className="border-l border-gray-300 h-12"></div>
              <div className="flex flex-col items-center text-center">
                <p className="font-semibold text-gray-700">Net Cash Flow</p>
                <p className={`text-lg font-bold ${totalNetCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Nu. {totalNetCashFlow.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(CashFlow);